/**
 * KPI Card Component
 * 
 * Modern, clean KPI display with subtle visual accents.
 */
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CurrencyDisplay } from './CurrencyDisplay';
import { TrendingUp, TrendingDown, Minus, HelpCircle, LucideIcon } from 'lucide-react';

interface KPICardProps {
  title: string;
  value: number;
  format?: 'currency' | 'percentage' | 'number' | 'days';
  previousValue?: number;
  icon?: LucideIcon;
  iconColor?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
  subtitle?: string;
  tooltip?: string;
  className?: string;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const variantConfig = {
  default: {
    accent: 'bg-primary/10',
    icon: 'text-primary',
    indicator: 'bg-primary',
  },
  success: {
    accent: 'bg-emerald-50 dark:bg-emerald-950/30',
    icon: 'text-emerald-600 dark:text-emerald-400',
    indicator: 'bg-emerald-500',
  },
  warning: {
    accent: 'bg-amber-50 dark:bg-amber-950/30',
    icon: 'text-amber-600 dark:text-amber-400',
    indicator: 'bg-amber-500',
  },
  danger: {
    accent: 'bg-rose-50 dark:bg-rose-950/30',
    icon: 'text-rose-600 dark:text-rose-400',
    indicator: 'bg-rose-500',
  },
};

const trendIcons = {
  up: TrendingUp,
  down: TrendingDown,
  neutral: Minus,
};

const trendColors = {
  up: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50',
  down: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/50',
  neutral: 'text-muted-foreground bg-muted',
};

export function KPICard({
  title,
  value,
  format = 'currency',
  previousValue,
  icon: Icon,
  iconColor,
  trend,
  trendLabel,
  subtitle,
  tooltip,
  className,
  variant = 'default',
}: Readonly<KPICardProps>) {
  const config = variantConfig[variant] || variantConfig.default;
  
  // Calculate trend if previousValue is provided and trend is not specified
  const compararComAnterior = () => {
    if (previousValue === undefined) return undefined;
    if (value > previousValue) return 'up';
    if (value < previousValue) return 'down';
    return 'neutral';
  };
  const calculatedTrend = trend || compararComAnterior();

  const TrendIcon = calculatedTrend ? trendIcons[calculatedTrend] : null;

  // Calculate percentage change
  const percentChange =
    previousValue !== undefined && previousValue !== 0
      ? ((value - previousValue) / Math.abs(previousValue)) * 100
      : undefined;

  // Detect negative values for highlighting
  const isNegative = value < 0;
  const negativeClass = isNegative ? 'text-rose-600 dark:text-rose-400' : '';

  const formatValue = () => {
    switch (format) {
      case 'currency':
        return (
          <CurrencyDisplay
            value={value}
            size="xl"
            colorize={isNegative}
            className={cn("font-semibold", negativeClass)}
          />
        );
      case 'percentage':
        return (
          <span className={cn("text-2xl font-semibold tracking-tight", negativeClass)}>
            {value.toFixed(1)}%
          </span>
        );
      case 'days':
        return (
          <span className={cn("text-2xl font-semibold tracking-tight", isNegative ? negativeClass : '')}>
            {value} {value === 1 ? 'dia' : 'dias'}
          </span>
        );
      case 'number':
      default:
        return (
          <span className={cn("text-2xl font-semibold tracking-tight", negativeClass)}>
            {value.toLocaleString('pt-BR')}
          </span>
        );
    }
  };

  return (
    <div
      className={cn(
        'relative bg-card rounded-xl border border-border/60 p-5 transition-all duration-200 hover:shadow-md hover:border-border group',
        className
      )}
    >
      {/* Subtle top indicator */}
      <div className={cn('absolute top-0 left-4 right-4 h-0.5 rounded-full', config.indicator, 'opacity-60')} />
      
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-3 flex-1 min-w-0">
          {/* Title with tooltip */}
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-muted-foreground truncate">{title}</h3>
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help shrink-0 hover:text-muted-foreground transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-sm">{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          {/* Value */}
          <div className="text-foreground">
            {formatValue()}
          </div>
          
          {/* Subtitle or trend */}
          <div className="flex items-center gap-2 min-h-[20px]">
            {calculatedTrend && TrendIcon && (
              <div
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                  trendColors[calculatedTrend]
                )}
              >
                <TrendIcon className="h-3 w-3" />
                {percentChange !== undefined && (
                  <span>{Math.abs(percentChange).toFixed(1)}%</span>
                )}
                {trendLabel && <span>{trendLabel}</span>}
              </div>
            )}
            {subtitle && !calculatedTrend && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
            {subtitle && calculatedTrend && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
        
        {/* Icon */}
        {Icon && (
          <div
            className={cn(
              'p-3 rounded-xl transition-transform duration-200 group-hover:scale-105',
              config.accent
            )}
          >
            <Icon className={cn('h-5 w-5', iconColor || config.icon)} />
          </div>
        )}
      </div>
    </div>
  );
}

export default KPICard;
